/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import {
  type CSSProperties,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FocusOn } from 'react-focus-on';
import { useEventListener } from 'usehooks-ts';
import MenuContextProvider from '@common/components/Menu/MenuContextProvider';
import Overlay from '@common/components/Overlay';
import useElementMaxSize, { type ForceDirection } from '@common/hooks/useElementMaxSize';
import classnames from '@common/utils/ui/classnames';
import getScrollbarSize from '@common/utils/ui/getScrollbarSize';
import '@common/styles/components/menu.pcss';

export type CloseMenu = (action?: () => void) => void;

export type MenuAlignment = 'left' | 'right';

export interface MenuProps {
  children: ReactNode | ((closeMenu: () => void) => ReactNode);
  renderSelector: (
    selectorRef: RefObject<HTMLDivElement | null>,
    toggleMenu: () => void,
    isOpen: boolean,
  ) => JSX.Element;
  align?: MenuAlignment;
  subMenu?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  forceDirection?: ForceDirection;
}

function Menu({
  children,
  renderSelector,
  align = 'left',
  subMenu,
  disabled,
  fullWidth,
  className = '',
  forceDirection,
  ...rest
}: Readonly<MenuProps>): JSX.Element {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const selectorRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [groupHasFocus, setGroupHasFocus] = useState(false);
  const [highlightedOptionIndex, setHighlightedOptionIndex] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_highlightedOption, setHighlightedOption] = useState<HTMLElement>(); // NOSONAR:S6754
  const groupRef = useRef<HTMLDivElement | null>(null);
  const [options, setOptions] = useState<HTMLElement[]>([]);
  const { maxHeight, maxWidth, openUpwards } = useElementMaxSize(menuRef, forceDirection, align);
  const [styles, setStyles] = useState<CSSProperties>({});
  const [previouslyFocused, setPreviouslyFocused] = useState<HTMLElement | null>(
    document.activeElement as HTMLElement,
  );

  const closeMenu = useCallback((action?: () => void) => {
    setIsOpen(false);
    setTimeout(() => {
      previouslyFocused?.focus();
      action?.();
    }, 1);
  }, [previouslyFocused]);

  const toggleMenu = useCallback(() =>
    setIsOpen((previous) => {
      if (!previous) {
        setPreviouslyFocused(document.activeElement as HTMLElement);
      }
      return !previous;
    }), []);

  const closeOnOutsideClick = useCallback((event: MouseEvent) => {
    if (
      isOpen
      && !(menuRef.current?.contains(event.target as Element)
        || selectorRef.current?.contains(event.target as Element))
    ) {
      closeMenu();
    }
  }, [closeMenu, isOpen]);

  useEventListener('pointerdown', closeOnOutsideClick);

  const top = useMemo(() => {
    if (fullWidth) {
      return (selectorRef?.current?.getBoundingClientRect()?.bottom ?? 0) + 6;
    }
    return undefined;
  }, [fullWidth]);

  const menuSelector = useMemo(
    () => renderSelector(selectorRef, toggleMenu, isOpen),
    [isOpen, selectorRef, renderSelector, toggleMenu],
  );

  useLayoutEffect(() => {
    let result: HTMLElement[] = [];
    if (groupRef.current && isOpen) {
      result = Array.from(
        groupRef.current.querySelectorAll('.menu__option'),
      ).filter((option) => {
        return !option.classList.contains('menu__option--disabled');
      }) as HTMLElement[];
      const index = result.findIndex((option) =>
        option.classList.contains('menu__option--selected')
      );
      setHighlightedOptionIndex(index !== -1 ? index : 0);
    }
    setOptions(result);
  }, [isOpen, children]);

  const isNotFocusable = useCallback((elem: HTMLElement | undefined) =>
    !elem?.querySelector(
      'input, button, select, textarea, [href], [tabindex]:not([tabindex="-1"])',
    ), []);

  const updateFocus = useCallback((elem: HTMLElement | undefined) => {
    setHighlightedOption((prev) => {
      if (prev) {
        if (isNotFocusable(prev)) {
          prev.removeAttribute('tabindex');
        }
        prev.classList.remove('menu__option--focused');
      }
      if (elem) {
        if (
          (
            elem.classList.contains('menu__option--custom')
            || elem.classList.contains('menu__option--toggle')
          )
          && !elem.querySelector(':focus')
        ) {
          (elem.querySelector(
            'input, button, select, textarea, [href], [tabindex]:not([tabindex="-1"])',
          ) as HTMLElement).focus();
        } else {
          elem.classList.add('menu__option--focused');
          if (isNotFocusable(elem)) {
            elem.setAttribute('tabindex', '0');
          }
          elem.focus();
        }
      }
      return elem;
    });
  }, [isNotFocusable]);

  useEffect(() => () => {
    options.forEach((option) => {
      if (isNotFocusable(option)) {
        option.removeAttribute('tabindex');
      }
    });
  }, [isNotFocusable, options]);

  useLayoutEffect(() => {
    updateFocus(options[highlightedOptionIndex]);
  }, [highlightedOptionIndex, options, updateFocus, isOpen]);

  const highlightNextOption = useCallback(() => {
    if (options.length) {
      let index = highlightedOptionIndex + 1;
      if (index >= options.length) {
        index = options.length - 1;
      }
      setHighlightedOptionIndex(index);
    }
  }, [highlightedOptionIndex, options.length]);

  const highlightPreviousOption = useCallback(() => {
    if (options.length) {
      let index = highlightedOptionIndex - 1;
      if (index < 0) {
        index = 0;
      }
      setHighlightedOptionIndex(index);
    }
  }, [highlightedOptionIndex, options.length]);

  const handleKeyNavigation = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (!disabled) {
      const { code } = event;
      if (code === 'ArrowDown') {
        highlightNextOption();
        event.preventDefault();
        event.stopPropagation();
      } else if (code === 'ArrowUp') {
        highlightPreviousOption();
        event.preventDefault();
        event.stopPropagation();
      } else if ((code === 'Escape' || (code === 'ArrowLeft' && subMenu)) && isOpen) {
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
      }
    }
  }, [closeMenu, disabled, highlightNextOption, highlightPreviousOption, isOpen, subMenu]);

  type OptionPosition = {
    elem: HTMLElement;
    rect: DOMRect;
  };

  type Size = {
    width: number;
    height: number;
  };

  const getToggleElementWidth = (elem: HTMLElement, fallbackWidth: number): number => {
    const menuOption = elem.querySelector('span');
    return menuOption?.getBoundingClientRect().width ?? fallbackWidth;
  };

  const getDivElementWidth = (elem: HTMLElement, fallbackWidth: number): number => {
    const menuOption = elem.querySelector('div');
    if (!menuOption) { return fallbackWidth; }
    let width = menuOption.getBoundingClientRect().width;
    if (elem.classList.contains('menu__option--select')) {
      width += elem.querySelector('.menu__option--select > svg')?.getBoundingClientRect().width
        ?? 0;
    }
    return width;
  };

  const getElementSize = (optionPosition: OptionPosition): Size => {
    const { elem, rect } = optionPosition;
    const isToggle = elem.classList.contains('menu__option--toggle');
    const width = isToggle
      ? getToggleElementWidth(elem, rect.width)
      : getDivElementWidth(elem, rect.width);
    return { width, height: rect.height };
  };

  const calculateColumnSizes = (
    optionPositions: OptionPosition[],
    lefts: Set<number>,
  ): Size[] =>
    [...lefts.values()].map((left) => {
      const sizes = optionPositions
        .filter((item) => item.rect.left === left)
        .map((item) => getElementSize(item));
      return sizes.reduce<Size>((acc, size) => ({
        width: Math.max(acc.width, size.width),
        height: acc.height + size.height,
      }), { width: 0, height: 0 });
    });

  const calculateMenuStyles = (
    groupElement: HTMLDivElement,
    menuMaxHeight: number | undefined,
    menuMaxWidth: number | undefined,
  ): CSSProperties => {
    const result: CSSProperties = {};
    const optionList = groupElement.querySelectorAll('.menu__option');
    if (optionList.length === 0) { return result; }

    const optionPositions = [...optionList].map((elem) => ({
      elem: elem as HTMLElement,
      rect: elem.getBoundingClientRect(),
    }));
    const lefts = new Set(optionPositions.map((item) => item.rect.left));
    const maxSizes = calculateColumnSizes(optionPositions, lefts);
    const optionStyle = window.getComputedStyle(groupElement.querySelector('.menu__option')!);

    if (lefts.size > 1) {
      const { marginLeft, marginRight, paddingLeft, paddingRight } = optionStyle;
      result.width = maxSizes.reduce((acc, size) =>
        acc + size.width
        + parseFloat(paddingLeft) + parseFloat(paddingRight)
        + parseFloat(marginLeft) + parseFloat(marginRight), 0);
    }

    if (menuMaxHeight == null) { return result; }

    result.maxHeight = menuMaxHeight;
    const { marginTop, marginBottom } = optionStyle;
    const maxColHeight = Math.max(...maxSizes.map((size) => size.height))
      + parseFloat(marginTop) + parseFloat(marginBottom);

    if (maxColHeight > menuMaxHeight) {
      result.overflowY = 'scroll';
      result.width = (result.width as number) + getScrollbarSize().width;
    }
    if (lefts.size > 1 && menuMaxWidth != null && (result.width as number) > menuMaxWidth) {
      result.maxWidth = menuMaxWidth;
      result.overflowX = 'scroll';
    }
    return result;
  };

  // Size the menu
  useLayoutEffect(() => {
    if (isOpen && groupRef.current) {
      setStyles(calculateMenuStyles(groupRef.current, maxHeight, maxWidth));
    }
  }, [groupRef.current, maxHeight, maxWidth, isOpen, children]);

  const memoChildren = useMemo(
    () => typeof children === 'function' ? children(closeMenu) : children,
    [children, closeMenu],
  );

  const menuContent = useMemo(() => (
    <MenuContextProvider closeMenu={closeMenu}>
      <div
        role="menu"
        tabIndex={0}
        ref={groupRef}
        onKeyDown={groupHasFocus ? handleKeyNavigation : undefined}
        onFocus={() => setGroupHasFocus(true)}
        onBlur={(event) => {
          if (!groupRef.current?.contains(event.relatedTarget)) {
            setGroupHasFocus(false);
          }
        }}
        className={classnames({
          [className]: !!className,
          menu__options: true,
          'menu__options--full-width': fullWidth,
          'menu__options--narrow': !fullWidth,
          'menu__options--upwards': openUpwards,
        })}
        style={{
          ...styles,
          minHeight: 'calc(100% + 12px)',
          top: subMenu && !openUpwards ? 0 : top,
          bottom: subMenu && openUpwards ? 0 : undefined,
          left: align === 'left' && !subMenu ? 0 : undefined,
          right: !fullWidth && align === 'right' ? 0 : undefined,
        }}
      >
        {memoChildren}
      </div>
    </MenuContextProvider>
  ), [
    align,
    className,
    closeMenu,
    fullWidth,
    groupHasFocus,
    handleKeyNavigation,
    memoChildren,
    openUpwards,
    styles,
    subMenu,
    top,
  ]);

  if (fullWidth) {
    return (
      <div
        ref={menuRef}
        className={classnames({
          menu: true,
          'menu--submenu': subMenu,
          'menu--disabled': disabled,
        })}
        {...rest}
      >
        {menuSelector}
        <Overlay
          show={isOpen}
          noAnimation
          style={{ top }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            closeMenu();
          }}
        >
          <FocusOn onEscapeKey={() => closeMenu()}>
            {menuContent}
          </FocusOn>
        </Overlay>
      </div>
    );
  }

  return (
    <div
      ref={menuRef}
      className={classnames({
        menu: true,
        'menu--submenu': subMenu,
        'menu--disabled': disabled,
      })}
      {...rest}
    >
      {menuSelector}
      {isOpen && (
        <FocusOn onEscapeKey={() => closeMenu()}>
          {menuContent}
        </FocusOn>
      )}
    </div>
  );
}

export default Menu;
